/**
 * Home hero carousel, focus grid, and quick nav — aligned with Services (`latestServices` themes).
 */

export const SERVICE_QUICK_NAV = [
  { to: '/web-development', label: 'Web development' },
  { to: '/ecommerce-development', label: 'Ecommerce' },
  { to: '/magento-development', label: 'Magento' },
  { to: '/ai-automation', label: 'AI automation' },
  { to: '/wordpress-development', label: 'WordPress' },
  { to: '/website-maintenance', label: 'Maintenance' },
  { to: '/portfolio', label: 'Portfolio' },
  { to: '/getintouch', label: 'Contact' },
];

/**
 * Hero CTAs: paths must match `src/App.js` routes.
 *
 * Image art: 16∶9 or wider at ≥1200px; hero uses a 50/50 split with full image visible (`object-fit: contain`).
 *
 * backgroundVideo: optional decorative Vimeo background, rendered muted/looped behind slide content.
 *
 * titleVariant: 'primary' (h1, first slide) | 'section' (h2, dark slides) | 'display' (h2 large on saturated bg)
 * ledeVariant: 'default' | 'inverse' (light text on slate/performance)
 *
 * ctaPalette: ties hero button colors to slide art (see `home-hero-slider.css` `.home-hero-slider__cta-palette--*`).
 */
export const HOME_HERO_SLIDES = [
  {
    id: 'web',
    ctaPalette: 'mesh',
    num: '01',
    nav: { title: 'Web & Product', subtitle: 'Design + engineering' },
    panelClass: 'main-slider-bg-light',
    slideClasses: 'swiper-slide home-hero-slider__slide home-hero-slider__slide--mesh',
    eyebrow: 'Plux Infosystems · Global delivery',
    eyebrowOnDark: false,
    titleVariant: 'primary',
    title: 'Web design and product engineering for global teams',
    ledeVariant: 'default',
    subtitle:
      'React and Next.js front ends, dependable APIs, and UX focused on clarity and conversion—fast, accessible, and easy to maintain.',
    image: {
      src: 'img/webdevelopment.png',
      priority: true,
      alt: 'Illustration of web design and product engineering',
    },
    backgroundVideo: {
      vimeoId: '1197030591',
      label: 'Decorative web design background animation',
    },
    ctas: [
      { to: '/web-design', label: 'Web design', variant: 'dark' },
      { to: '/web-development', label: 'Development', variant: 'ghost' },
    ],
  },
  {
    id: 'performance',
    ctaPalette: 'slate',
    num: '02',
    nav: { title: 'Performance', subtitle: 'Speed + quality' },
    panelClass: 'home-hero-slider__nav-thumb--slate',
    slideClasses:
      'swiper-slide home-hero-slider__slide home-hero-slider__slide--performance main-slider-bg-dark',
    eyebrow: 'Speed and reliability',
    eyebrowOnDark: true,
    titleVariant: 'section',
    title: 'Faster sites and healthier Core Web Vitals',
    ledeVariant: 'inverse',
    subtitle:
      'We tighten front ends, fix bottlenecks, and refine critical paths so your product feels quick under real traffic—not just in demos.',
    image: { src: 'img/slider1.png', alt: 'Website performance and Core Web Vitals optimization' },
    backgroundVideo: {
      vimeoId: '1197030607',
      label: 'Decorative search engine optimization background animation',
    },
    ctas: [
      { to: '/web-development', label: 'Engineering services', variant: 'dark' },
      { to: '/services', label: 'Full catalog', variant: 'lightOutline' },
    ],
  },
  {
    id: 'ai',
    ctaPalette: 'teal',
    num: '03',
    nav: { title: 'AI Automation', subtitle: 'Bots + workflows' },
    panelClass: 'bg-primary-color',
    slideClasses: 'swiper-slide bg-primary-color main-slider-bg-dark',
    eyebrow: 'Intelligent systems',
    eyebrowOnDark: true,
    titleVariant: 'section',
    title: 'AI automation that fits your stack',
    ledeVariant: 'default',
    subtitle:
      'Chatbots, voice flows, and workflow automation with CRM hooks, guardrails, and reporting—less manual work, clearer handoffs.',
    image: { src: 'img/slider2.png', alt: 'AI automation and workflow integration' },
    backgroundVideo: {
      vimeoId: '1197030588',
      label: 'Decorative automation workflow background animation',
    },
    ctas: [
      { to: '/ai-automation', label: 'AI automation services', variant: 'dark' },
      { to: '/getintouch', label: 'Plan a use case', variant: 'primary-sm' },
    ],
  },
  {
    id: 'ecom',
    ctaPalette: 'coral',
    num: '04',
    nav: { title: 'Ecommerce', subtitle: 'Magento + stores' },
    panelClass: 'bg-secondary-color',
    slideClasses: 'swiper-slide bg-secondary-color main-slider-bg-dark',
    eyebrow: 'Stores and operations',
    eyebrowOnDark: true,
    titleVariant: 'display',
    title: 'Magento and ecommerce delivery',
    ledeVariant: 'default',
    subtitle:
      'Adobe Commerce builds, catalog UX, checkout, integrations, and order flows that keep up with traffic and ops.',
    image: { src: 'img/slider3.png', alt: 'Magento and ecommerce platform delivery' },
    backgroundVideo: {
      vimeoId: '1197030589',
      label: 'Decorative ecommerce background animation',
    },
    ctas: [
      { to: '/ecommerce-development', label: 'Ecommerce development', variant: 'dark' },
      { to: '/magento-development', label: 'Magento', variant: 'border' },
    ],
  },
  {
    id: 'backend',
    ctaPalette: 'amber',
    num: '05',
    nav: { title: 'APIs & Backends', subtitle: 'Python + Node' },
    panelClass: 'bg-orange-color',
    slideClasses: 'swiper-slide bg-orange-color main-slider-bg-dark',
    eyebrow: 'Integration layer',
    eyebrowOnDark: true,
    titleVariant: 'display',
    title: 'APIs, services, and integrations',
    ledeVariant: 'default',
    subtitle:
      'REST and event-driven backends, queues, webhooks, and ERP or CRM connections—with security, logging, and deployment in mind.',
    image: { src: 'img/slider4.png', alt: 'API and backend integration services' },
    backgroundVideo: {
      vimeoId: '1197032473',
      label: 'Decorative API services integration background animation',
    },
    ctas: [
      { to: '/web-development', label: 'Backend and APIs', variant: 'dark' },
      { to: '/services', label: 'More capabilities', variant: 'borderRaised' },
    ],
  },
  {
    id: 'mobile',
    ctaPalette: 'forest',
    num: '06',
    nav: { title: 'Mobile Apps', subtitle: 'React Native' },
    panelClass: 'bg-green-color',
    slideClasses: 'swiper-slide bg-green-color main-slider-bg-dark',
    eyebrow: 'Cross-platform',
    eyebrowOnDark: true,
    titleVariant: 'display',
    title: 'React Native for iOS and Android',
    ledeVariant: 'default',
    subtitle:
      'One codebase for both stores: native APIs, push, deep links, and release workflows your team can run with confidence.',
    image: { src: 'img/slider5.png', alt: 'React Native mobile app development' },
    backgroundVideo: {
      vimeoId: '1197036923',
      label: 'Decorative mobile app development background animation',
    },
    ctas: [
      { to: '/mobile-app', label: 'Mobile apps', variant: 'dark' },
      { to: '/getintouch', label: 'Start a project', variant: 'borderRaised' },
    ],
  },
];

const FOCUS_ICONS = [
  { img: 'img/info-box30.png', alt: 'AI and automation icon' },
  { img: 'img/info-box29.png', alt: 'Chatbot and messaging icon' },
  { img: 'img/info-box1.png', alt: 'Voice and audio interface icon' },
  { img: 'img/info-box2.png', alt: 'Python development icon' },
  { img: 'img/info-box3.png', alt: 'Node.js and API icon' },
  { img: 'img/info-box4.png', alt: 'Next.js web applications icon' },
  { img: 'img/info-box5.png', alt: 'React frontend icon' },
  { img: 'img/info-box6.png', alt: 'React Native mobile icon' },
  { img: 'img/info-box7.png', alt: 'WordPress Roots Sage theme icon' },
];

/** Mirrors priority capabilities from Services `latestServices` */
export const HOME_FOCUS_AREAS = [
  {
    title: 'AI solutions & automation',
    desc: 'LLM-powered workflows, content generation, support triage, and ops automation—integrated with your CRM, forms, and reporting stack.',
    to: '/ai-automation',
    ...FOCUS_ICONS[0],
  },
  {
    title: 'AI chatbot development',
    desc: 'Conversational bots for lead capture, FAQs, and handoffs—wired to Salesforce, HubSpot, or custom APIs with guardrails and analytics.',
    to: '/ai-automation',
    ...FOCUS_ICONS[1],
  },
  {
    title: 'AI voice assistants',
    desc: 'Voice interfaces for enquiry routing, appointment booking, and support flows with speech recognition, intent handling, and fallbacks.',
    to: '/ai-automation',
    ...FOCUS_ICONS[2],
  },
  {
    title: 'Python development',
    desc: 'FastAPI and Django services, data pipelines, ETL jobs, and automation scripts—tested, containerized, and ready for cloud deployment.',
    to: '/web-development',
    ...FOCUS_ICONS[3],
  },
  {
    title: 'Node.js backend engineering',
    desc: 'Express and NestJS APIs, event-driven workers, webhooks, and real-time integrations built for scale, security, and observability.',
    to: '/web-development',
    ...FOCUS_ICONS[4],
  },
  {
    title: 'Next.js web applications',
    desc: 'App Router sites with SSR, SSG, and ISR—optimized Core Web Vitals, SEO-ready structure, and modular component architecture.',
    to: '/web-development',
    ...FOCUS_ICONS[5],
  },
  {
    title: 'React frontend development',
    desc: 'Component libraries, state management, design systems, and performance tuning for dashboards, portals, and customer-facing apps.',
    to: '/web-development',
    ...FOCUS_ICONS[6],
  },
  {
    title: 'React Native mobile apps',
    desc: 'Cross-platform iOS and Android with native modules, push notifications, deep links, and CI/CD pipelines for store releases.',
    to: '/mobile-app',
    ...FOCUS_ICONS[7],
  },
  {
    title: 'Roots Sage WordPress themes',
    desc: 'Expert custom WordPress theme builds with Roots Sage, Blade templates, Vite asset pipelines, Tailwind CSS, ACF, and maintainable component architecture.',
    to: '/wordpress-development',
    ...FOCUS_ICONS[8],
  },
];
